/**
 * Tenant authentication policy — SSOT: tenants.settings.auth
 * @see .cursor/plans/archived/2026-q2/genesis-funnel-completion/identity-federation-design.md §4.5
 */

import { z } from 'zod';

export const TENANT_SOCIAL_OAUTH_PROVIDERS = [
  'google',
  'github',
  'microsoft',
  'apple',
  'kakao',
  'facebook',
] as const;

export type TenantSocialOAuthProvider = (typeof TENANT_SOCIAL_OAUTH_PROVIDERS)[number];

export const TENANT_OAUTH_MODES = ['platform_managed', 'tenant_owned'] as const;

export type TenantOAuthMode = (typeof TENANT_OAUTH_MODES)[number];

/** OAuth callback providers with tenant-owned client config (Phase 6 BYOIDP). */
export const TENANT_OAUTH_CLIENT_PROVIDERS = ['google', 'apple', 'kakao'] as const;

export type TenantOAuthClientProvider = (typeof TENANT_OAUTH_CLIENT_PROVIDERS)[number];

const PLAINTEXT_OAUTH_SECRET_FIELD_NAMES = new Set([
  'clientSecret',
  'client_secret',
  'secret',
  'oauthClientSecret',
]);

export class TenantAuthPolicyPlaintextSecretError extends Error {
  readonly code = 'PLAINTEXT_OAUTH_SECRET_FORBIDDEN' as const;

  constructor(readonly paths: readonly string[]) {
    super(`Plaintext OAuth client secrets are forbidden (use secretRef): ${paths.join(', ')}`);
    this.name = 'TenantAuthPolicyPlaintextSecretError';
  }
}

function collectPlaintextOAuthSecretPaths(value: unknown, path: string[] = []): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }

  const violations: string[] = [];
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    const currentPath = [...path, key];
    if (PLAINTEXT_OAUTH_SECRET_FIELD_NAMES.has(key)) {
      violations.push(currentPath.join('.'));
      continue;
    }
    if (nested && typeof nested === 'object') {
      violations.push(...collectPlaintextOAuthSecretPaths(nested, currentPath));
    }
  }
  return violations;
}

/** Reject oauthClients entries that store plaintext secrets — secretRef only. */
export function assertNoPlaintextOAuthClientSecrets(raw: unknown): void {
  const paths = collectPlaintextOAuthSecretPaths(raw);
  if (paths.length > 0) {
    throw new TenantAuthPolicyPlaintextSecretError(paths);
  }
}

export const tenantOAuthClientConfigSchema = z
  .object({
    clientId: z.string().trim().min(1),
    secretRef: z.string().trim().min(1).optional(),
  })
  .strict();

export type TenantOAuthClientConfig = z.infer<typeof tenantOAuthClientConfigSchema>;

export const tenantOAuthClientsSchema = z
  .object({
    google: tenantOAuthClientConfigSchema.optional(),
    apple: tenantOAuthClientConfigSchema.optional(),
    kakao: tenantOAuthClientConfigSchema.optional(),
  })
  .strict()
  .optional();

export type TenantOAuthClients = z.infer<typeof tenantOAuthClientsSchema>;

export const tenantAuthPolicySchema = z.object({
  allowPlatformSSO: z.boolean().default(true),
  allowLocalPassword: z.boolean().default(true),
  /** OAuth login for existing members (provider buttons on /login). */
  allowedSocialProviders: z.array(z.enum(TENANT_SOCIAL_OAUTH_PROVIDERS)).default(['google']),
  /**
   * OAuth self-signup for strangers without invite/membership (Population B).
   * Default false — join-approved members OAuth-login via allowedSocialProviders only.
   */
  allowSocialSignup: z.boolean().default(false),
  oauthMode: z.enum(TENANT_OAUTH_MODES).default('platform_managed'),
  oauthClients: tenantOAuthClientsSchema,
});

export type TenantAuthPolicy = z.infer<typeof tenantAuthPolicySchema>;

/** Effective policy returned to login UI (defaults when settings.auth absent). */
export const DEFAULT_TENANT_AUTH_POLICY: TenantAuthPolicy = {
  allowPlatformSSO: true,
  allowLocalPassword: true,
  allowedSocialProviders: ['google'],
  allowSocialSignup: false,
  oauthMode: 'platform_managed',
};

export function getTenantOAuthMode(policy: TenantAuthPolicy): TenantOAuthMode {
  return policy.oauthMode ?? 'platform_managed';
}

export function getTenantOAuthClientConfig(
  policy: TenantAuthPolicy,
  provider: TenantOAuthClientProvider
): TenantOAuthClientConfig | undefined {
  return policy.oauthClients?.[provider];
}

export function parseTenantAuthPolicy(raw: unknown): TenantAuthPolicy {
  assertNoPlaintextOAuthClientSecrets(raw);
  const parsed = tenantAuthPolicySchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  return DEFAULT_TENANT_AUTH_POLICY;
}

export function extractTenantAuthPolicyFromSettings(
  settings: Record<string, unknown> | null | undefined
): TenantAuthPolicy {
  if (!settings || typeof settings !== 'object') {
    return DEFAULT_TENANT_AUTH_POLICY;
  }
  return parseTenantAuthPolicy(settings.auth);
}

export function isGoogleOAuthExposed(policy: TenantAuthPolicy): boolean {
  return policy.allowPlatformSSO || policy.allowedSocialProviders.includes('google');
}

export function isSocialLoginAllowed(
  policy: TenantAuthPolicy,
  provider: TenantSocialOAuthProvider
): boolean {
  return policy.allowedSocialProviders.includes(provider);
}

export function isSocialSignupAllowed(
  policy: TenantAuthPolicy,
  provider: TenantSocialOAuthProvider
): boolean {
  return policy.allowSocialSignup && isSocialLoginAllowed(policy, provider);
}

export function isOperatorOAuthAllowed(policy: TenantAuthPolicy): boolean {
  return policy.allowPlatformSSO;
}
